import { useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router'
import { ChevronDown, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { moduleForPath, navForModuleFiltered, platformModules, visibleModules } from './nav'
import { useAuth } from '@/app/core/auth/AuthProvider'

export function Sidebar({
  collapsed,
  onToggle,
}: {
  collapsed: boolean
  onToggle: () => void
}) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { user } = useAuth()
  const [moduleOpen, setModuleOpen] = useState(false)
  const activeModule = moduleForPath(pathname)
  const modules = visibleModules(user?.roles ?? [])
  const current =
    modules.find((m) => m.key === activeModule) ??
    platformModules.find((m) => m.key === activeModule) ??
    modules[0] ??
    platformModules[0]
  const nav = navForModuleFiltered(activeModule, user?.roles ?? [])
  const CurrentIcon = current.icon

  return (
    <aside
      className="fixed left-0 top-0 z-40 h-screen flex flex-col bg-white/80 backdrop-blur-xl border-r border-border overflow-x-hidden transition-[width] duration-200 ease-in-out"
      style={{ width: collapsed ? 72 : 248 }}
    >
      {/* Brand + collapse toggle — toggle pinned left so nothing teleports on toggle */}
      <div className="flex items-center gap-2 h-[64px] shrink-0 px-3">
        <button
          onClick={onToggle}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-black/[0.04] hover:text-foreground transition-colors shrink-0"
        >
          <PanelToggleIcon open={!collapsed} />
        </button>
        <img
          src="/xlongevity-logo.png"
          alt="xLongevity"
          className={cn(
            'h-[15px] w-auto block object-contain object-left shrink-0 transition-opacity duration-150',
            collapsed ? 'opacity-0' : 'opacity-100',
          )}
        />
      </div>

      {/* Module switcher */}
      <div className="px-3 pb-3 shrink-0">
        <DropdownMenu open={moduleOpen} onOpenChange={setModuleOpen}>
          <DropdownMenuTrigger asChild>
            <button
              title={collapsed ? `Module: ${current.label}` : undefined}
              className="w-full flex items-center gap-2.5 rounded-[14px] border border-border bg-accent/60 hover:bg-accent transition-colors px-2 py-2"
            >
              <span className="w-7 h-7 rounded-lg bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <CurrentIcon className="w-4 h-4" />
              </span>
              {!collapsed && (
                <>
                  <span className="flex-1 text-left text-[14px] font-semibold text-foreground whitespace-nowrap">{current.label}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className={collapsed ? 'min-w-[200px]' : undefined}
            style={collapsed ? undefined : { width: 'var(--radix-dropdown-menu-trigger-width)' }}
          >
            {modules.map((m) => {
              const Icon = m.icon
              const isCurrent = m.key === activeModule
              return (
                <DropdownMenuItem
                  key={m.key}
                  className="cursor-pointer flex items-center gap-2.5 py-2"
                  onSelect={() => navigate(m.to)}
                >
                  <span className="w-7 h-7 rounded-lg bg-muted text-foreground/70 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="flex-1 flex items-center gap-1.5 text-[13.5px] font-semibold text-foreground">
                    {m.label}
                    {!m.built && (
                      <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground bg-muted rounded px-1 py-0.5">Soon</span>
                    )}
                  </span>
                  {isCurrent && <Check className="w-4 h-4 text-primary shrink-0" />}
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Nav — icons stay at a fixed left x; labels clip/reveal as width animates */}
      <nav className="flex-1 overflow-x-hidden overflow-y-auto hide-scrollbar px-3 py-1">
        {nav.map((section, i) => (
          <div key={i} className="mb-1.5">
            {section.heading && (
              collapsed
                ? i > 0 && <div className="mx-2 my-2 border-t border-border" />
                : <div className="px-3 pt-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground/70 whitespace-nowrap">{section.heading}</div>
            )}
            {section.items.map((item) => {
              const Icon = item.icon
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  title={collapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-[12px] mb-0.5 px-2.5 py-2.5 text-[13.5px] font-medium transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary font-semibold'
                        : 'text-foreground/70 hover:bg-black/[0.035] hover:text-foreground',
                    )
                  }
                >
                  <Icon className="w-[18px] h-[18px] shrink-0" />
                  {!collapsed && <span className="whitespace-nowrap">{item.label}</span>}
                </NavLink>
              )
            })}
          </div>
        ))}
      </nav>

    </aside>
  )
}

/** Sidebar frame whose left column is filled when open, empty when collapsed. */
function PanelToggleIcon({ open }: { open: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="2.5" />
      <line x1="9" y1="3" x2="9" y2="21" />
      {open && <rect x="4.4" y="4.4" width="3.2" height="15.2" rx="1" fill="currentColor" stroke="none" />}
    </svg>
  )
}
